
import { useState, useCallback } from 'react';
import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query';
import { supabase } from '@/integrations/supabase/client';
import { toast } from 'sonner';

interface BrokenGameResult {
  id: string;
  title: string;
  game_url: string;
  status: 'checking' | 'broken' | 'working' | 'error';
  error?: string;
}

interface BrokenGamesDetectionResult {
  total: number;
  checked: number;
  broken: BrokenGameResult[];
  working: BrokenGameResult[];
  errors: BrokenGameResult[];
}

export const useBrokenGamesDetection = () => {
  const [isScanning, setIsScanning] = useState(false);
  const [progress, setProgress] = useState(0);
  const [results, setResults] = useState<BrokenGamesDetectionResult>({
    total: 0,
    checked: 0,
    broken: [],
    working: [],
    errors: []
  });
  const queryClient = useQueryClient();

  // Fetch all games for scanning
  const { data: allGames = [] } = useQuery({
    queryKey: ['all-games-for-scanning'],
    queryFn: async () => {
      const { data, error } = await supabase
        .from('games')
        .select('id, title, game_url, hidden')
        .eq('hidden', false)
        .not('game_url', 'is', null)
        .not('game_url', 'eq', '');
      
      if (error) throw error;
      return data || [];
    },
    enabled: false // Only fetch when manually triggered
  });

  // Function to fetch ALL games from the database (handles pagination)
  const fetchAllGames = async () => {
    let allGames: { id: string; title: string; game_url: string; hidden: boolean }[] = [];
    let from = 0;
    const batchSize = 1000;
    let hasMore = true;

    console.log('Starting to fetch all games...');

    while (hasMore) {
      const { data, error } = await supabase
        .from('games')
        .select('id, title, game_url, hidden')
        .eq('hidden', false)
        .not('game_url', 'is', null)
        .not('game_url', 'eq', '')
        .range(from, from + batchSize - 1)
        .order('created_at', { ascending: false });

      if (error) {
        console.error('Error fetching games batch:', error);
        throw error;
      }

      if (data && data.length > 0) {
        allGames = [...allGames, ...data];
        console.log(`Fetched batch ${Math.floor(from / batchSize) + 1}: ${data.length} games (total so far: ${allGames.length})`);
        from += batchSize;
        hasMore = data.length === batchSize;
      } else {
        hasMore = false;
      }
    }

    console.log(`Finished fetching all games. Total: ${allGames.length}`);
    return allGames;
  };

  // Function to check if a game URL is working
  const checkGameUrl = async (game: { id: string; title: string; game_url: string }): Promise<BrokenGameResult> => {
    try {
      // First check if the URL is valid
      if (!game.game_url || game.game_url.trim() === '') {
        return {
          id: game.id,
          title: game.title,
          game_url: game.game_url,
          status: 'broken',
          error: 'Empty or null URL'
        };
      }

      // Try to fetch the URL with a timeout
      const controller = new AbortController();
      const timeoutId = setTimeout(() => controller.abort(), 10000); // 10 second timeout

      try {
        const response = await fetch(game.game_url, {
          method: 'HEAD', // Use HEAD to avoid downloading the entire content
          signal: controller.signal,
          mode: 'no-cors' // This will help with CORS issues
        });

        clearTimeout(timeoutId);

        // For no-cors mode, we can't check the actual status, so we assume it's working
        // if the fetch didn't throw an error
        return {
          id: game.id,
          title: game.title,
          game_url: game.game_url,
          status: 'working'
        };
      } catch (fetchError) {
        clearTimeout(timeoutId);
        
        // Try with a different approach - check if it's a relative URL or malformed
        try {
          new URL(game.game_url);
        } catch {
          return {
            id: game.id,
            title: game.title,
            game_url: game.game_url,
            status: 'broken',
            error: 'Invalid URL format'
          };
        }

        // If it's a network error, timeout, or CORS issue, mark as broken
        const errorMessage = fetchError instanceof Error ? fetchError.message : 'Unknown error';
        return {
          id: game.id,
          title: game.title,
          game_url: game.game_url,
          status: 'broken',
          error: errorMessage
        };
      }
    } catch (error) {
      return {
        id: game.id,
        title: game.title,
        game_url: game.game_url,
        status: 'error',
        error: error instanceof Error ? error.message : 'Unknown error'
      };
    }
  };

  // Main scanning function
  const scanForBrokenGames = useCallback(async () => {
    if (isScanning) return;
    
    setIsScanning(true);
    setProgress(0);
    setResults({
      total: 0,
      checked: 0,
      broken: [],
      working: [],
      errors: []
    });

    try {
      // Fetch ALL games using our custom function
      const games = await fetchAllGames();

      if (games.length === 0) {
        toast.info('No games found to scan');
        return;
      }

      const totalGames = games.length;
      const newResults: BrokenGamesDetectionResult = {
        total: totalGames,
        checked: 0,
        broken: [],
        working: [],
        errors: []
      };

      setResults(newResults);
      toast.info(`Starting scan of ${totalGames} games...`);

      // Process games in batches to avoid overwhelming the browser
      const batchSize = 5;
      for (let i = 0; i < games.length; i += batchSize) {
        const batch = games.slice(i, i + batchSize);
        
        const batchPromises = batch.map(game => checkGameUrl(game));
        const batchResults = await Promise.all(batchPromises);
        
        batchResults.forEach(result => {
          if (result.status === 'broken') {
            newResults.broken.push(result);
          } else if (result.status === 'working') {
            newResults.working.push(result);
          } else {
            newResults.errors.push(result);
          }
        });

        newResults.checked += batch.length;
        setProgress((newResults.checked / totalGames) * 100);
        setResults({ ...newResults });

        // Small delay between batches to prevent overwhelming the browser
        await new Promise(resolve => setTimeout(resolve, 100));
      }

      toast.success(`Scan complete! Found ${newResults.broken.length} broken games out of ${totalGames} checked.`);
    } catch (error) {
      console.error('Error scanning games:', error);
      toast.error('Failed to scan games: ' + (error instanceof Error ? error.message : 'Unknown error'));
    } finally {
      setIsScanning(false);
      setProgress(100);
    }
  }, [isScanning]);

  // Mutation to hide broken games
  const hideBrokenGamesMutation = useMutation({
    mutationFn: async (gameIds: string[]) => {
      const { error } = await supabase
        .from('games')
        .update({ hidden: true })
        .in('id', gameIds);
      
      if (error) throw error;
    },
    onSuccess: (_, gameIds) => {
      toast.success(`Hidden ${gameIds.length} broken games`);
      queryClient.invalidateQueries({ queryKey: ['admin-games'] });
    },
    onError: (error) => {
      toast.error('Failed to hide broken games: ' + (error instanceof Error ? error.message : 'Unknown error'));
    }
  });

  return {
    isScanning,
    progress,
    results,
    scanForBrokenGames,
    hideBrokenGames: hideBrokenGamesMutation.mutate,
    isHidingBrokenGames: hideBrokenGamesMutation.isPending
  };
};
